/**
 * @lines98/client/screens/mode-select-screen — the LOBBY («Играть», extra-modes
 * §5.1, fix-cycle P5 redesign — owner-vision 2026-06-03).
 *
 * «Веточка» (CLAUDE.md «ствол → ветки»): тонкая ветка, монтирует общие модули и
 * задаёт только раскладку. Полноразмерной доски тут НЕТ (C1). Лобби = семейства
 * режимов (из `lib/mode-registry` — единый источник, не копии) с плашками:
 *   - «Обычная игра»: Предсказание / Классика / Вслепую — unlocked, играбельно.
 *   - «Соревновательный режим»: на время / гонка с призраком — locked «Скоро».
 *   - «Мета-режим»: платный плейсхолдер — locked «Скоро».
 *
 * Owner 2026-06-03 (п.7): в лобби НЕТ заголовка «Играть», описания/«?» и кнопки
 * «Назад» — навигация через nav-rail (там подсвечено «Играть»). Остаются только
 * ЗАГОЛОВКИ СЕМЕЙСТВ + плашки. Клик по unlocked-плашке → игровой экран режима в
 * pre-start превью (`lib/mode-preview` — lazy-on-preview T-A: серверная сессия
 * выдаётся НА ВХОД в превью, превью буквально = зачётная партия); «Начать» (рейл) =
 * `preStart→false` без перекладки доски. На игровой доске кнопок выбора режима НЕТ (§5.2).
 *
 * Reused modules (AC-16): `lib/mode-registry` (каталог), `components/mode-card`
 * (плашка + board-view-миниатюра + «?»-popover пояснения), `lib/autofit-text`
 * (заголовки семейств). Spec: extra-modes-alltime.md §5.1; responsive SKILL §0/§5.
 */

import '../styles/mode-select.css';

import type { GameMode } from '@lines98/shared/types/game';

import { createModeCard, type ModeCardHandle } from '../components/mode-card.js';
import { createText } from '../components/text.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import { t, getLocale } from '../lib/i18n/index.js';
import { openModePreview } from '../lib/mode-preview.js';
import {
  FAMILY_LABEL,
  LOBBY_FAMILIES,
  lobbyEntriesOfFamily,
  modeLabel,
  type ModeEntry,
} from '../lib/mode-registry.js';
import type { ContentLang } from '@lines98/shared/config/seo';
import type { ScreenHandle } from './board-screen.js';

export function mountModeSelectScreen(host: HTMLElement): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'mode-select-screen screen-root';
  // REVEAL-GATE (white-flash fix B, owner-playtest 2026-06-24 «при „Играть“ внизу
  // появляется белое, потом исчезает»): монтируем СКРЫТО (opacity:0 в потоке —
  // mode-select.css), чтобы доски-превью в плашках растеризовались и раскладка
  // устаканилась ПОКА скрыто; атрибут снимаем double-rAF ниже → контент проявляется
  // целиком, без мелькания пустой структуры. Канон «render hidden, reveal when ready».
  root.dataset.mounting = '';
  const groupsEl = document.createElement('div');
  groupsEl.className = 'mode-select-groups';
  root.appendChild(groupsEl);
  host.appendChild(root);

  const cards: ModeCardHandle[] = [];

  // Одна секция на семейство (только непустые). Плашки — прямо из реестра (без копий).
  // lobbyEntriesOfFamily пропускает leaderboardOnly-сущности (борд «Мастер-ювелир» —
  // он в «Рекордах», не в лобби; Срез 3b).
  for (const family of LOBBY_FAMILIES) {
    const entries = lobbyEntriesOfFamily(family);
    if (entries.length === 0) continue;

    const section = document.createElement('section');
    section.className = 'mode-select-group';

    // Заголовок семейства — примитив text, роль 'title' (крупнее подписей плашек;
    // размер-токен --fs-title наследуется). owner-request 2026-06-04: «шрифт группы
    // побольше / хотя бы как на досках».
    const heading = createText({
      role: 'title',
      weight: 800,
      as: 'h3',
      text: FAMILY_LABEL[family],
      className: 'mode-select-group-title',
    }).element;
    section.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'mode-select-cards';
    // role=group (НЕ list): плашка сама = role=button (createModeCard).
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', FAMILY_LABEL[family]);

    for (const entry of entries) {
      const card = buildCard(entry);
      grid.appendChild(card.element);
      cards.push(card);
    }

    section.appendChild(grid);
    groupsEl.appendChild(section);
  }

  // Заголовки семейств — autofit (shrink-to-fit, никогда не crop; §0.A). Плашки
  // (имя/«?») НЕ autofit'им — единый шрифт из cqw (owner: одинаковый шрифт у всех).
  const autofit: AutofitHandle = attachAutofit(root, [
    { selector: '.mode-select-group-title', capToDesign: true },
  ]);

  // Раскладка/cqw-доски/превью резолвятся ПОКА скрыто; снимаем reveal-gate через
  // double-rAF (после settle) → контент проявляется СРАЗУ финальным, без мелькания.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      root.removeAttribute('data-mounting');
    }),
  );

  return {
    unmount(): void {
      autofit.detach();
      for (const c of cards) c.destroy();
      host.innerHTML = '';
    },
  };
}

/** Build a mode-card from a registry entry (locked → inert; unlocked → starts preview). */
function buildCard(entry: ModeEntry): ModeCardHandle {
  // Имя режима — локализованное (modeLabel, единый источник), НЕ ru-дефолт entry.label.
  // Лобби — фаза, ремонтируется на смену языка → getLocale() резолвит актуальный язык.
  const name = modeLabel(entry, getLocale() as ContentLang);
  return createModeCard({
    entry,
    hint: entry.hint,
    ariaLabel: entry.locked
      ? `${name} (${entry.badge ?? t('game.lobby.ariaUnavailable')}): ${entry.hint}`
      : `${name}: ${entry.hint}`,
    onSelect: entry.mode ? () => startMode(entry.mode!) : () => {},
  });
}

/**
 * Open the non-daily game screen in PRE-START PREVIEW of `mode` (T-A, lazy-on-preview):
 * a fresh server session is allocated NOW (`POST /game/start`, server seed) and the
 * board mounts on it in preview — the LITERAL scored partition. «Начать» (rail) then
 * begins it with NO reshuffle (AC-A.2). Shared module `lib/mode-preview` owns the call
 * (one source — reused by re-roll + «Играть ещё»). Fire-and-forget: the card click is
 * sync; failures surface a toast inside the module.
 */
function startMode(mode: GameMode): void {
  void openModePreview(mode);
}
